import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Copy, ShieldCheck, KeyRound, Users, RefreshCw, Eye, EyeOff, CheckCircle2, Building2, LogIn } from "lucide-react";
import StatusPill from "@/components/aieco/StatusPill";
import { useAuth } from "@/context/AuthContext";

const PROVIDERS = [
  { id: "okta", name: "Okta", domain: "atlas.okta.com", users: 2148, enabled: true, color: "#5B8DEF" },
  { id: "azure", name: "Azure AD / Entra", domain: "atlas.onmicrosoft.com", users: 0, enabled: false, color: "#22C55E" },
  { id: "google", name: "Google Workspace", domain: "atlas.com", users: 0, enabled: false, color: "#F59E0B" },
];

const SYNC_LOG = [
  { time: "06:24 UTC", who: "Okta SCIM", what: "Provisioned 4 new users (Risk Ops team)" },
  { time: "06:24 UTC", who: "Okta SCIM", what: "Deactivated 2 users (offboarded)" },
  { time: "05:00 UTC", who: "Okta SCIM", what: "Group sync: ai-reviewers (38 members)" },
  { time: "Yesterday", who: "Okta SCIM", what: "Initial bootstrap · 2,144 users imported" },
];

const SCIM_TOKEN = "scim_eco_sk_live_8a4f2bd9c1e7a4b6f0d3e9c2af1b6e0d4c5a9b8e3f7c1d6a9b2";

export default function SettingsPage() {
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [providers, setProviders] = useState(PROVIDERS);
  const { user, login, logout } = useAuth();
  const { t } = useTranslation();

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(SCIM_TOKEN);
    } catch (e) {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = SCIM_TOKEN;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const toggle = (id) => setProviders((p) => p.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-settings">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("settings.subtitle")}</div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">{t("settings.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            {t("settings.lede")}
          </p>
        </div>
        <StatusPill tone="success">SOC 2 Type II · ISO 27001</StatusPill>
      </motion.div>

      {/* Active Admin Session Card */}
      <div className="card-eco mt-6 p-6" data-testid="admin-session-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/5 bg-white/[0.02] text-[#5B8DEF]">
              <LogIn size={18} strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-base text-white">Admin session</div>
              <div className="mt-0.5 text-[12px] text-slate-400">
                {user
                  ? `Signed in as ${user.email} — full SCIM administration & rotation enabled.`
                  : "Sign in with your Google admin account to manage providers, rotate SCIM tokens and view sync logs."}
              </div>
            </div>
          </div>
          {user ? (
            <div className="flex items-center gap-3">
              {user.picture && <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full" data-testid="admin-avatar" />}
              <div className="hidden text-right text-[12px] sm:block">
                <div className="text-slate-100" data-testid="admin-name">{user.name}</div>
                <div className="text-slate-500">{user.email}</div>
              </div>
              <button
                onClick={logout}
                data-testid="settings-sign-out"
                className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[12px] text-slate-200 hover:border-white/10"
              >
                Sign out
              </button>
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="settings-sign-in"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[12px] font-medium text-[#0b1020] hover:bg-slate-100"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 001 12c0 1.78.42 3.46 1.18 4.94l3.66-2.84z"/>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              </svg>
              Sign in with Google
            </button>
          )}
        </div>
      </div>

      {/* Providers */}
      <div className="mt-7 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {providers.map((p) => (
          <div key={p.id} className="card-eco card-eco-hover p-5" data-testid={`provider-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/5 bg-white/[0.02] font-display text-[13px] text-white" style={{ background: `linear-gradient(180deg, ${p.color}22, ${p.color}05)`, color: p.color }}>
                  {p.name.split(/\s|\//)[0].slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="font-display text-base text-white">{p.name}</div>
                  <div className="font-mono text-[11px] text-slate-500">{p.domain}</div>
                </div>
              </div>
              <button
                onClick={() => toggle(p.id)}
                data-testid={`toggle-${p.id}`}
                className={`relative h-5 w-9 rounded-full border transition ${p.enabled ? "border-[#5B8DEF]/40 bg-[#5B8DEF]" : "border-white/10 bg-white/[0.04]"}`}
              >
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${p.enabled ? "left-[18px]" : "left-0.5"}`} />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
              <div className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Users synced</div>
                <div className="mt-1 font-mono text-lg text-white">{p.users.toLocaleString()}</div>
              </div>
              <div className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Status</div>
                <div className="mt-1">
                  {p.enabled ? <StatusPill tone="success">Connected</StatusPill> : <StatusPill tone="neutral" dot={false}>Disabled</StatusPill>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SCIM Token */}
      <div className="card-eco mt-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/5 bg-white/[0.02] text-[#5B8DEF]">
              <KeyRound size={18} strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-base text-white">SCIM 2.0 Bearer token</div>
              <div className="mt-0.5 text-[12px] text-slate-400">Paste this into your IdP&rsquo;s SCIM provisioning URL configuration.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setReveal((r) => !r)} data-testid="toggle-scim-reveal" className="inline-flex items-center gap-1.5 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-slate-200 hover:border-white/10">
              {reveal ? <EyeOff size={12} /> : <Eye size={12} />}
              {reveal ? "Hide" : "Reveal"}
            </button>
            <button onClick={copyToken} data-testid="copy-scim-token" className="inline-flex items-center gap-1.5 rounded-lg bg-[#5B8DEF] px-2.5 py-1.5 text-[12px] font-medium text-[#0b1020] hover:bg-[#7AA4F5]">
              {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button data-testid="rotate-scim-token" className="inline-flex items-center gap-1.5 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-slate-200 hover:border-white/10">
              <RefreshCw size={12} /> Rotate
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-white/5 bg-[#0b1020]/60 px-3 py-3">
          <code className="block break-all font-mono text-[12px] text-slate-200" data-testid="scim-token-value">
            {reveal ? SCIM_TOKEN : "•".repeat(SCIM_TOKEN.length)}
          </code>
        </div>
        <div className="mt-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 text-[12px] text-slate-400">
          <span className="font-mono text-slate-300">SCIM endpoint:</span>{" "}
          <span className="font-mono text-[#5B8DEF]">https://api.ai-eco.io/scim/v2/atlas</span>
        </div>
      </div>

      {/* Sync log */}
      <div className="card-eco mt-6 p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Sync log</div>
          <span className="font-mono text-[11px] text-slate-500">last 24h</span>
        </div>
        <ul className="space-y-1.5">
          {SYNC_LOG.map((l, i) => (
            <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 text-[12px]">
              <div className="flex items-center gap-3">
                <Users size={13} className="text-[#5B8DEF]" />
                <span className="text-slate-200">{l.what}</span>
              </div>
              <span className="font-mono text-[10px] text-slate-500">{l.who} · {l.time}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
